import React, { useMemo } from 'react';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { BaseComponentState, ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

type StackDirection = 'vertical' | 'horizontal';
type StackAlign = 'start' | 'end' | 'center' | 'stretch' | 'baseline';
type SpacingSize = ComponentSize | '2xl' | 'none';
type ContainerElement =
  | 'div'
  | 'section'
  | 'article'
  | 'aside'
  | 'nav'
  | 'header'
  | 'footer'
  | 'main'
  | 'ul'
  | 'ol'
  | 'form'
  | 'fieldset';

interface StackContainerProps extends ForwardedHTMLProps, BaseComponentState {
  children: React.ReactNode;
  className?: string;
  as?: ContainerElement;
  direction?: StackDirection;
  spacing?: SpacingSize;
  align?: StackAlign;
  justify?: 'start' | 'end' | 'center' | 'between';
  dividers?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  style?: React.CSSProperties;
  onSubmit?: (e: React.FormEvent) => void;
}

const verticalSpacingSizes: Record<SpacingSize, string> = {
  none: '',
  xs: 'space-y-1',
  sm: 'space-y-2',
  md: 'space-y-4',
  lg: 'space-y-6',
  xl: 'space-y-8',
  '2xl': 'space-y-10',
};

const horizontalSpacingSizes: Record<SpacingSize, string> = {
  none: '',
  xs: 'space-x-1',
  sm: 'space-x-2',
  md: 'space-x-4',
  lg: 'space-x-6',
  xl: 'space-x-8',
  '2xl': 'space-x-10',
};

const alignMap: Record<StackAlign, string> = {
  start: 'items-start',
  end: 'items-end',
  center: 'items-center',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

const justifyMap: Record<string, string> = {
  start: 'justify-start',
  end: 'justify-end',
  center: 'justify-center',
  between: 'justify-between',
};

export const StackContainer = React.forwardRef<HTMLDivElement, StackContainerProps>(
  (
    {
      children,
      className,
      as = 'div',
      direction = 'vertical',
      spacing = 'md',
      align = 'stretch',
      justify,
      dividers = false,
      fullWidth = false,
      fullHeight = false,
      loading = false,
      disabled = false,
      style,
      onSubmit,
      ...props
    },
    ref
  ) => {
    const forwardedProps = forwardProps(props);

    const Component = as as React.ElementType;

    const isVertical = direction === 'vertical';

    const containerClasses = useMemo(() => {
      const spacingClass = isVertical ? verticalSpacingSizes[spacing] : horizontalSpacingSizes[spacing];

      return cn(
        'flex',
        isVertical ? 'flex-col' : 'flex-row',
        spacingClass,
        alignMap[align],
        justify && justifyMap[justify],
        dividers &&
          (isVertical
            ? 'divide-y divide-slate-200 dark:divide-slate-700'
            : 'divide-x divide-slate-200 dark:divide-slate-700'),
        fullWidth && 'w-full',
        fullHeight && 'h-full',
        loading && 'animate-pulse',
        disabled && 'opacity-50 pointer-events-none',
        className
      );
    }, [isVertical, spacing, align, justify, dividers, fullWidth, fullHeight, loading, disabled, className]);

    return (
      <Component
        ref={ref}
        style={style}
        className={containerClasses}
        aria-busy={loading || undefined}
        aria-disabled={disabled || undefined}
        onSubmit={as === 'form' ? onSubmit : undefined}
        {...forwardedProps}
        {...props}
      >
        {children}
      </Component>
    );
  }
);

StackContainer.displayName = 'StackContainer';

export default StackContainer;
